package dev.terrakok.mapitan.domain

import androidx.compose.ui.graphics.Color
import com.rafambn.kflate.KFlate
import com.rafambn.kflate.RAW
import com.rafambn.kflate.Raw
import kotlinx.serialization.KSerializer
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.*
import org.maplibre.spatialk.geojson.Point
import org.maplibre.spatialk.geojson.Position
import kotlin.io.encoding.Base64
import kotlin.math.roundToLong

private const val COORDINATE_SCALE = 100_000.0

object DocumentSerialization {
    private val json = Json {
        ignoreUnknownKeys = true
    }

    private val base64 = Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT_OPTIONAL)

    fun encode(document: Document): String {
        val jsonBytes = json.encodeToString(document).encodeToByteArray()
        val compressed = KFlate.compress(jsonBytes, RAW())
        return base64.encode(compressed)
    }

    fun decode(value: String): Document {
        val json = KFlate.decompress(base64.decode(value), Raw()).decodeToString()
        return this.json.decodeFromString(json)
    }
}



internal class ColorSerializer : KSerializer<Color> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("Color", PrimitiveKind.LONG)

    override fun serialize(encoder: Encoder, value: Color) = encoder.encodeLong(value.value.toLong())

    override fun deserialize(decoder: Decoder): Color = Color(decoder.decodeLong().toULong())
}

internal class PointSerializer : KSerializer<Point> {
    override val descriptor: SerialDescriptor = ListSerializer(Long.serializer()).descriptor

    override fun serialize(encoder: Encoder, value: Point) {
        require(encoder is JsonEncoder) { "PointSerializer supports JSON only" }
        encoder.encodeJsonElement(
            JsonArray(
                buildList {
                    add(JsonPrimitive((value.longitude * COORDINATE_SCALE).roundToLong()))
                    add(JsonPrimitive((value.latitude * COORDINATE_SCALE).roundToLong()))
                    value.altitude?.let { add(JsonPrimitive(it.roundToLong())) }
                }
            )
        )
    }

    override fun deserialize(decoder: Decoder): Point {
        require(decoder is JsonDecoder) { "PointSerializer supports JSON only" }
        val coordinates = decoder.decodeJsonElement() as JsonArray
        require(coordinates.size in 2..3) { "Point must contain longitude, latitude and optional altitude" }
        return Point(
            Position(
                longitude = coordinates[0].jsonPrimitive.long / COORDINATE_SCALE,
                latitude = coordinates[1].jsonPrimitive.long / COORDINATE_SCALE,
                altitude = coordinates.getOrNull(2)?.jsonPrimitive?.long?.toDouble(),
            )
        )
    }
}

internal class TrackPointsDeltaSerializer : KSerializer<List<TrackPoint>> {
    override val descriptor: SerialDescriptor =
        ListSerializer(TrackPoint.serializer()).descriptor

    override fun serialize(encoder: Encoder, value: List<TrackPoint>) {
        require(encoder is JsonEncoder) { "TrackPointsDeltaSerializer supports JSON only" }

        val elements = buildJsonArray {
            value.forEachIndexed { index, trackPoint ->
                val point = trackPoint.point
                val lon = point.longitude
                val lat = point.latitude
                val alt = point.altitude

                if (index == 0) {
                    // First point: store absolute coordinates
                    add(
                        buildJsonObject {
                            put("id", JsonPrimitive(trackPoint.id))
                            putJsonArray("c") {
                                add(JsonPrimitive((lon * COORDINATE_SCALE).roundToLong()))
                                add(JsonPrimitive((lat * COORDINATE_SCALE).roundToLong()))
                                alt?.let { add(JsonPrimitive(it.roundToLong())) }
                            }
                        }
                    )
                } else {
                    // Subsequent points: store delta from previous
                    val prev = value[index - 1].point
                    val prevAlt = prev.altitude
                    val dLon = lon - prev.longitude
                    val dLat = lat - prev.latitude
                    val dAlt = if (alt != null) {
                        if (prevAlt != null) alt - prevAlt else alt
                    } else {
                        null
                    }

                    add(
                        buildJsonObject {
                            put("id", JsonPrimitive(trackPoint.id))
                            putJsonArray("dc") {
                                add(JsonPrimitive((dLon * COORDINATE_SCALE).roundToLong()))
                                add(JsonPrimitive((dLat * COORDINATE_SCALE).roundToLong()))
                                dAlt?.let { add(JsonPrimitive(it.roundToLong())) }
                            }
                        }
                    )
                }
            }
        }

        encoder.encodeJsonElement(elements)
    }

    override fun deserialize(decoder: Decoder): List<TrackPoint> {
        require(decoder is JsonDecoder) { "TrackPointsDeltaSerializer supports JSON only" }
        val json = decoder.decodeJsonElement() as JsonArray

        return json.foldIndexed(mutableListOf<Pair<Point, String>>()) { index, acc, _ ->
            val obj = json[index] as JsonObject
            val id = obj["id"]?.jsonPrimitive?.content
                ?: error("Missing 'id' at index $index")
            val coords = (obj["c"] ?: obj["dc"]) as? JsonArray
                ?: error("Missing coordinates at index $index")

            val rawLon = coords[0].jsonPrimitive.long
            val rawLat = coords[1].jsonPrimitive.long
            val hasAlt = coords.size > 2

            if (index == 0) {
                // First point: absolute coordinates
                val alt = if (hasAlt) coords[2].jsonPrimitive.long.toDouble() else null
                val point = Point(Position(rawLon / COORDINATE_SCALE, rawLat / COORDINATE_SCALE, alt))
                acc.add(point to id)
            } else {
                // Accumulate deltas from previous point
                val (prev, _) = acc[index - 1]
                val dLon = coords[0].jsonPrimitive.long.toDouble() / COORDINATE_SCALE
                val dLat = coords[1].jsonPrimitive.long.toDouble() / COORDINATE_SCALE
                val dAlt = if (hasAlt) coords[2].jsonPrimitive.long.toDouble() else null

                val accLon = prev.longitude + dLon
                val accLat = prev.latitude + dLat
                val accAlt = if (dAlt != null) {
                    prev.altitude?.let { it + dAlt } ?: dAlt
                } else null

                acc.add(Point(Position(accLon, accLat, accAlt)) to id)
            }
            acc
        }.map { (point, id) -> TrackPoint(id, point) }
    }
}
